import { useForm } from '@tanstack/react-form'
import { AlertCircle } from 'lucide-react'
import { inventoryReasonLabels, inventoryReasons } from '../constants/inventory'
import { InventoryOutTransactionCreateSchema } from '../schema/inventorySchema'
import { useRecordOutTransactionMutation } from '../hooks'
import { useInventoryStore } from '../store/inventoryStore'
import type { InventoryOutTransactionCreateData } from '../schema/inventorySchema'
import type { InventoryOutReason } from '../types/inventory'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { usePermissions } from '@/utils/permissions'
import { useIsMobile } from '@/hooks'
import { FormTextarea } from '@/components/custom/form-textarea'
import { Modal } from '@/components/custom/modal'
import { UserRoleEnum } from '@/features/users/types/user'
import { useUsersStore } from '@/features/users/store/usersStore'

interface InventoryOutTransactionFormProps {
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  itemId?: string | null
}

export function InventoryOutTransactionForm({
  trigger,
  open,
  onClose,
  itemId,
}: InventoryOutTransactionFormProps) {
  const isMobile = useIsMobile()

  const { mutateAsync: recordOutTransactionMutation, isPending } =
    useRecordOutTransactionMutation()

  const user = useAuthStore((state) => state.user)
  const permissions = usePermissions()

  const churches = useChurchesStore((state) => state.churches)
  const userChurches = permissions.isSystemAdmin()
    ? churches
    : churches.filter((church) => user?.churchId?.includes(church._id))
  const inventoryStock = useInventoryStore((state) => state.inventoryStock)
  const categories = useInventoryStore((state) => state.categories)
  const pastors = useUsersStore((state) => state.users).filter((u) =>
    u.roles.includes(UserRoleEnum.PASTOR),
  )

  const initialItem = inventoryStock.find((item) => item._id === itemId)
  const initialChurch = churches.find(
    (church) => church._id === initialItem?.churchId,
  )

  const form = useForm({
    defaultValues: {
      type: 'OUT' as const,
      churchId: initialChurch?._id || '',
      categoryId: initialItem?.categoryId || '',
      item: initialItem?.item || '',
      unit: initialItem?.unit || '',
      brand: initialItem?.brand || '',
      description: initialItem?.description || '',
      quantity: 1,
      disposal: {
        reason: '' as InventoryOutReason,
        authorizedBy: '',
      },
      notes: '',
      inventoryStockId: initialItem?._id || '',
    } as InventoryOutTransactionCreateData,
    onSubmit: async ({ value }) => {
      await recordOutTransactionMutation(value)
      handleClose()
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  // Filter items to only show those with quantity > 0
  const availableItems = inventoryStock.filter(
    (item) =>
      item.quantity > 0 &&
      (user?.roles.includes(UserRoleEnum.SYSTEM_ADMIN) ||
        user?.churchId?.includes(item.churchId)),
  )

  const handleItemNameSelect = (itemName: string) => {
    const selectedItem = inventoryStock.find((item) => item.item === itemName)
    if (selectedItem) {
      form.setFieldValue('categoryId', selectedItem.categoryId)
      form.setFieldValue('unit', selectedItem.unit || '')
      form.setFieldValue('brand', selectedItem.brand || '')
      form.setFieldValue('description', selectedItem.description || '')
    }
    form.setFieldValue('item', itemName)
  }

  const churchOptions = userChurches.filter((church) => {
    return availableItems.some((item) => item.churchId === church._id)
  })

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title="Remove Inventory"
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      {availableItems.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle
              className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-amber-600`}
            />
            <span
              className={`font-medium text-amber-800 ${isMobile ? 'text-sm' : ''}`}
            >
              No items available to remove from inventory. Please add inventory
              items first.
            </span>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          <form.Field
            name="churchId"
            children={(field) => {
              return (
                <div className="gap-0">
                  <AutoCompleteSelect
                    field={field}
                    label="Church"
                    id={field.name}
                    name={field.name}
                    options={churchOptions.map((church) => ({
                      value: church._id,
                      label: church.name,
                    }))}
                    required
                  />
                </div>
              )
            }}
          />

          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending
              const churchPastors = pastors.filter((pastor) =>
                pastor.churchId?.includes(churchId),
              )

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Item Select */}
                  <form.Field
                    name="item"
                    validators={{
                      onBlur: ({ value }) => {
                        const result =
                          InventoryOutTransactionCreateSchema.shape.item.safeParse(
                            value,
                          )
                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => {
                      const churchStock = availableItems.filter(
                        (item) => item.churchId === churchId,
                      )
                      return (
                        <AutoCompleteSelect
                          field={{
                            ...field,
                            handleChange: handleItemNameSelect,
                          }}
                          label="Item"
                          id={field.name}
                          name={field.name}
                          disabled={isFieldsDisabled}
                          options={churchStock.map((stockItem) => {
                            return {
                              value: stockItem.item,
                              label: `${stockItem.item}${stockItem.brand ? ` (${stockItem.brand})` : ''} - ${stockItem.quantity} ${stockItem.unit}`,
                              group: categories.find(
                                (cat) => cat._id === stockItem.categoryId,
                              )?.name,
                            }
                          })}
                          required
                        />
                      )
                    }}
                  />

                  {/* Quantity */}
                  <form.Field
                    name="quantity"
                    validators={{
                      onBlur: ({ value }) => {
                        const result =
                          InventoryOutTransactionCreateSchema.shape.quantity.safeParse(
                            value,
                          )

                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => (
                      <form.Subscribe
                        selector={(state) => state.values.item}
                        children={(item) => {
                          const quantityAvailable = availableItems.find(
                            (i) => i.item === item,
                          )?.quantity
                          return (
                            <FormInput
                              field={field}
                              label="Quantity"
                              type="number"
                              min="1"
                              max={
                                quantityAvailable
                                  ? Number(quantityAvailable)
                                  : undefined
                              }
                              onBlur={field.handleBlur}
                              disabled={isFieldsDisabled}
                              required
                            />
                          )
                        }}
                      />
                    )}
                  />

                  {/* Reason */}
                  <form.Field
                    name="disposal.reason"
                    validators={{
                      onBlur: ({ value }) => {
                        const result =
                          InventoryOutTransactionCreateSchema.shape.disposal.shape.reason.safeParse(
                            value,
                          )

                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => (
                      <div className="gap-0">
                        <AutoCompleteSelect
                          field={field}
                          label="Reason"
                          id={field.name}
                          name={field.name}
                          options={inventoryReasons.map((reason) => ({
                            value: reason,
                            label: inventoryReasonLabels[reason],
                          }))}
                          disabled={isFieldsDisabled}
                          required
                        />
                      </div>
                    )}
                  />

                  {/* Authorized By */}
                  <form.Field
                    name="disposal.authorizedBy"
                    children={(field) => (
                      <div className="gap-0">
                        <AutoCompleteSelect
                          field={field}
                          label="Authorized By"
                          id={field.name}
                          name={field.name}
                          options={churchPastors.map((pastor) => ({
                            value: pastor._id,
                            label: pastor.name,
                          }))}
                          disabled={isFieldsDisabled}
                          required
                        />
                      </div>
                    )}
                  />
                </div>
              )
            }}
          />

          {/* Notes */}
          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending
              return (
                <form.Field
                  name="notes"
                  children={(field) => (
                    <div className="gap-0">
                      <FormTextarea
                        field={field}
                        label="Notes"
                        id={field.name}
                        name={field.name}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Additional notes (optional)"
                        disabled={isFieldsDisabled}
                      />
                    </div>
                  )}
                />
              )
            }}
          />

          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending
              return (
                <div className="flex gap-2 justify-end">
                  <LoadingButton
                    isLoading={isPending}
                    disabled={isFieldsDisabled || availableItems.length === 0}
                    type="submit"
                  >
                    {isPending ? 'Submitting...' : 'Submit'}
                  </LoadingButton>
                </div>
              )
            }}
          />
          {/* Submit Button */}
        </form>
      )}
    </Modal>
  )
}
